import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from 'lucide-react';

export type QuickDateRange = 'custom' | 'today' | 'yesterday' | 'last-week' | 'current-month';

interface DateRange {
  from: string;
  to: string;
}

interface DateRangeQuickFilterProps {
  value: QuickDateRange;
  onChange: (range: QuickDateRange, dates: DateRange) => void;
  reportType?: string;
  className?: string;
}

export const DateRangeQuickFilter: React.FC<DateRangeQuickFilterProps> = ({
  value,
  onChange,
  reportType,
  className = ''
}) => {
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateRange = (range: QuickDateRange): DateRange => {
    const today = new Date();
    const todayStr = formatLocalDate(today);

    switch (range) {
      case 'today':
        return { from: todayStr, to: todayStr };

      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return { from: formatLocalDate(yesterday), to: formatLocalDate(yesterday) };

      case 'last-week':
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 6);
        return {
          from: formatLocalDate(lastWeek),
          to: todayStr
        };

      case 'current-month':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          from: formatLocalDate(firstDayOfMonth),
          to: todayStr
        };

      case 'custom':
      default:
        return { from: '', to: '' };
    }
  };

  const handleRangeChange = (newRange: QuickDateRange) => {
    const dates = getDateRange(newRange);
    onChange(newRange, dates);

    // Guardar preferencia en localStorage si es necesario
    if (reportType && newRange !== 'custom') {
      localStorage.setItem(`dateRange_${reportType}`, newRange);
    }
  };

  const getRangeLabel = (range: QuickDateRange): string => {
    switch (range) {
      case 'today':
        return 'Día Actual';
      case 'yesterday':
        return 'Día de Ayer';
      case 'last-week':
        return 'Última Semana';
      case 'current-month':
        return 'Mes en Curso';
      case 'custom':
        return 'Personalizado';
      default:
        return 'Personalizado';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Select value={value} onValueChange={handleRangeChange}>
        <SelectTrigger>
          <SelectValue>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {getRangeLabel(value)}
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="today">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Día Actual
            </div>
          </SelectItem>
          <SelectItem value="yesterday">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Día de Ayer
            </div>
          </SelectItem>
          <SelectItem value="last-week">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Última Semana
            </div>
          </SelectItem>
          <SelectItem value="current-month">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Mes en Curso
            </div>
          </SelectItem>
          <SelectItem value="custom">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Personalizado
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};